import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Bell, LogOut, ChevronLeft, ChevronRight, StickyNote,
  Eye, Shield, Search, XCircle, ChevronDown,
} from 'lucide-react';
import api from '@/lib/api';
import ForceRefreshBanner from '@/components/ForceRefreshBanner';
import { NotificationsModal } from '@/components/NotificationsModal';
import { ClipboardModal } from '@/components/ClipboardModal';
import { CommandPalette } from '@/components/CommandPalette';

// ─── Nav definition ───────────────────────────────────────────────────────
// Standalone items sit at the top; groups render as collapsible dropdowns.
// Each item declares a per-user `visible(effectiveRole, hasPerm)` predicate;
// a group is rendered only when at least one child is visible.
const standaloneNav = [
  { name: 'Dashboard', path: '/', visible: () => true },
  { name: 'Calendar', path: '/calendar', visible: () => true },
  { name: 'Employees', path: '/employees', visible: (role) => role === 'Administrator' },
];

const navGroups = [
  {
    name: 'Pipeline',
    children: [
      { name: 'Leads', path: '/leads', visible: () => true },
      { name: 'Proposals', path: '/proposals', visible: () => true },
    ],
  },
  {
    name: 'Operations',
    children: [
      { name: 'Contracts', path: '/contracts', visible: () => true },
      { name: 'Projects', path: '/projects', visible: () => true },
      { name: 'To-Dos', path: '/tasks', visible: () => true },
      { name: 'Documents', path: '/documents', visible: () => true },
      { name: 'Clients', path: '/clients', visible: () => true },
      { name: 'Resources', path: '/resources', visible: () => true },
      { name: 'Library', path: '/library', visible: () => true },
    ],
  },
  {
    name: 'Hours & Expenses',
    children: [
      { name: 'Timesheets', path: '/timesheets', visible: () => true },
      { name: 'Reimbursements', path: '/reimbursements', visible: () => true },
    ],
  },
  {
    name: 'Administrative',
    children: [
      { name: 'Invoices', path: '/invoices', visible: (role) => role !== 'Employee' },
      { name: 'Accounting', path: '/accounting', visible: (_role, hasPerm) => !!hasPerm?.('view_accounting') },
      { name: 'Status Updates', path: '/status-updates', visible: (_role, hasPerm) => !!hasPerm?.('view_status_log_global') },
      { name: 'Activity Log', path: '/activity-log', visible: (role) => ['Administrator', 'Owner', 'Office Manager'].includes(role) },
    ],
  },
  {
    name: 'Digital Marketing',
    children: [
      { name: 'Overview', path: '/digital-marketing', visible: () => true },
      { name: 'SEO Workspace', path: '/digital-marketing/seo-workspace', visible: () => true },
      { name: 'Content & Social', path: '/digital-marketing/content-social', visible: () => true },
      { name: 'Analytics & Research', path: '/digital-marketing/analytics', visible: () => true },
    ],
  },
];

const futureNav = [
  { name: 'Messages' },
];

export default function Layout() {
  const { user, logout, effectiveRole, viewAsRole, setViewAsRole, isRealAdmin, ALL_ROLES, hasPerm, previewPerms, stopPermPreview } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [clipboardModalOpen, setClipboardModalOpen] = useState(false);
  const [clipboardInboxUnread, setClipboardInboxUnread] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Detect light/dark mode to swap logo (color in light, inverted black -> white in dark)
  const [isDarkMode, setIsDarkMode] = useState(true);

  // ─── Sidebar group visibility + expand/collapse state ─────────────────
  // Filter each group's children by the current user's visibility rules.
  // A group is rendered only when at least one child is visible.
  const visibleGroups = navGroups
    .map((g) => ({
      ...g,
      children: g.children.filter((c) => c.visible(effectiveRole, hasPerm)),
    }))
    .filter((g) => g.children.length > 0);

  const visibleStandalone = standaloneNav.filter((i) => i.visible(effectiveRole, hasPerm));

  // Group currently containing the active route auto-expands. Any manual
  // toggles the user makes afterwards persist for the session.
  const activeGroupName = (() => {
    for (const g of visibleGroups) {
      if (g.children.some((c) => location.pathname === c.path
        || (c.path !== '/' && location.pathname.startsWith(`${c.path}/`)))) {
        return g.name;
      }
    }
    return null;
  })();

  const [expandedGroups, setExpandedGroups] = useState(() => (
    activeGroupName ? { [activeGroupName]: true } : {}
  ));

  // If the user navigates into a different group's page (e.g. via a link
  // outside the sidebar), auto-expand that group without collapsing others.
  useEffect(() => {
    if (activeGroupName && !expandedGroups[activeGroupName]) {
      setExpandedGroups((prev) => ({ ...prev, [activeGroupName]: true }));
    }
  }, [activeGroupName, expandedGroups]);

  const toggleGroup = (name) => {
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  useEffect(() => {
    api.get('/notifications').then(r => setNotifications(r.data)).catch(() => {});
    api.get('/clipboard').then(r => {
      setClipboardInboxUnread((r.data?.inbox || []).filter(i => !i.is_read).length);
    }).catch(() => {});
    // Watch theme attribute (set elsewhere — defaults to dark)
    const apply = () => setIsDarkMode(document.documentElement.getAttribute('data-theme') !== 'light');
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  // Global Cmd+K / Ctrl+K shortcut to open the command palette.
  // Ignored when the user is already typing in an input/textarea so it
  // doesn't hijack normal browser shortcuts (which use Cmd+K to focus the
  // URL bar). Pressing Cmd+K from outside an input opens the palette.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Build a short summary of OFF action perms for the preview banner.
  const previewOffSummary = (() => {
    if (!previewPerms) return null;
    const labels = {
      approve_proposals: 'Approve Proposals',
      approve_timesheets: 'Approve Timesheets',
      approve_invoices: 'Approve Invoices',
      approve_reimbursements: 'Approve Reimbursements',
      view_accounting: 'View Accounting',
      manage_clients: 'Manage Clients',
      manage_employees: 'Manage Employees',
      manage_digital_marketing: 'Manage Digital Marketing',
    };
    const offKeys = Object.entries(labels).filter(([k]) => !previewPerms[k]).map(([, v]) => v);
    return offKeys.length ? offKeys.join(' · ') : 'all action perms enabled';
  })();

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background relative">
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* LOGO COLUMN — absolutely positioned top-left, 200×64,
          independent of topbar height + sidebar collapse state.
          Sits one z-layer above sidebar/topbar so the logo can be
          larger than the 52px topbar without stretching it.
          The first sidebar nav item is at y=72 (52 topbar + 20
          paddingTop), so the 64-tall logo never overlaps nav text. */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div
        className="absolute top-0 left-0 z-30 flex items-center justify-start"
        style={{
          width: '200px',
          height: '64px',
          paddingLeft: '20px',
          paddingRight: '12px',
          backgroundColor: 'var(--sidebar-bg)',
          borderRight: '1px solid rgba(255, 255, 255, 0.07)',
        }}
        data-testid="logo-column"
      >
        <NavLink to="/" className="flex items-center w-full" data-testid="topbar-logo-link">
          <img
            src={isDarkMode ? '/nycd-logo-color-dark.png' : '/nycd-logo-color.png'}
            alt="NYCD Group"
            style={{
              height: '56px',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
            data-testid="topbar-logo"
          />
        </NavLink>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* TOP BAR — 52px, fixed full-width, persistent
          Left padding pushes the right-side action group past
          the 200px logo column. */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <header
        className="flex items-center justify-end border-b border-white/10 shrink-0"
        style={{ height: '52px', paddingLeft: '212px', paddingRight: '20px', backgroundColor: 'var(--sidebar-bg)' }}
        data-testid="topbar"
      >
        {/* Right side — search, notifications, role switcher, user */}
        <div className="flex items-center gap-2">
          {/* Search affordance — clickable hint + Cmd+K shortcut */}
          <Button
            variant="outline"
            size="sm"
            className="gap-2 h-8 text-muted-foreground hover:text-foreground border-white/15 px-3"
            onClick={() => setPaletteOpen(true)}
            data-testid="topbar-search-btn"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden md:inline text-xs">Search</span>
            <kbd className="hidden md:inline-flex items-center font-mono text-[10px] px-1.5 py-0.5 border border-white/15 rounded ml-1">
              {navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K'}
            </kbd>
          </Button>

          {/* Role Switcher (Admin only) */}
          {isRealAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={viewAsRole ? "default" : "ghost"}
                  size="sm"
                  className={`gap-1.5 text-xs ${viewAsRole ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="role-switcher-btn"
                >
                  <Eye className="h-3.5 w-3.5" />
                  {viewAsRole ? `Viewing: ${viewAsRole}` : 'View as Role'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border w-48">
                <div className="px-3 py-1.5">
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Preview a role</p>
                </div>
                <DropdownMenuSeparator />
                {ALL_ROLES.map(role => (
                  <DropdownMenuItem
                    key={role}
                    onClick={() => setViewAsRole(role === 'Administrator' ? null : role)}
                    className={`text-xs gap-2 ${effectiveRole === role ? 'bg-primary/10 text-primary' : ''}`}
                    data-testid={`role-option-${role.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {role === 'Administrator' && <Shield className="h-3 w-3" />}
                    {role !== 'Administrator' && <Eye className="h-3 w-3" />}
                    {role}
                    {role === user?.role && <Badge variant="outline" className="ml-auto text-xs py-0 px-1">You</Badge>}
                    {effectiveRole === role && <span className="ml-auto text-primary text-xs">Active</span>}
                  </DropdownMenuItem>
                ))}
                {viewAsRole && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setViewAsRole(null)}
                      className="text-xs text-primary gap-2"
                      data-testid="reset-role-btn"
                    >
                      <Shield className="h-3 w-3" /> Back to Administrator
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Clipboard */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setClipboardModalOpen(true)}
            data-testid="clipboard-icon-btn"
            title="Clipboard"
          >
            <StickyNote className="h-4 w-4 text-muted-foreground" />
            {clipboardInboxUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-red-500 text-xs font-bold flex items-center justify-center text-white" data-testid="clipboard-inbox-badge">
                {clipboardInboxUnread}
              </span>
            )}
          </Button>

          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setNotifModalOpen(true)}
            data-testid="notification-bell"
          >
            <Bell className="h-4 w-4 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-xs font-bold flex items-center justify-center text-primary-foreground">
                {unreadCount}
              </span>
            )}
          </Button>
          <NotificationsModal
            open={notifModalOpen}
            onOpenChange={setNotifModalOpen}
            notifications={notifications}
            setNotifications={setNotifications}
            onOpenClipboard={() => setClipboardModalOpen(true)}
          />
          <ClipboardModal
            open={clipboardModalOpen}
            onOpenChange={setClipboardModalOpen}
            onInboxCountChange={setClipboardInboxUnread}
          />
          <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground" data-testid="user-menu">
                <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                  {user?.name?.charAt(0)}
                </div>
                {user?.name}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border">
              <DropdownMenuItem className="text-xs text-muted-foreground">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Badge variant="outline" className="text-xs">{user?.role}</Badge>
                {viewAsRole && <Badge className="ml-2 text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">Viewing: {viewAsRole}</Badge>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                <LogOut className="h-3 w-3 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* PERMISSION PREVIEW BANNER — sticky strip shown when an admin
          is previewing a proposed OM permission set (set via the
          Employees → Permissions panel "Preview" button). */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {previewPerms && (
        <div
          className="flex items-center justify-between px-5 py-2 border-b border-amber-500/40 text-amber-100 shrink-0"
          style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)' }}
          data-testid="perm-preview-banner"
        >
          <div className="flex items-center gap-2 text-xs">
            <Eye className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span className="font-semibold uppercase tracking-wider text-amber-400">Previewing action permissions</span>
            <span className="text-amber-100/80 truncate">
              OFF: {previewOffSummary}
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 border-amber-400/40 text-amber-100 hover:bg-amber-500/20 hover:text-amber-50 gap-1.5 text-xs"
            onClick={stopPermPreview}
            data-testid="exit-perm-preview-btn"
          >
            <XCircle className="h-3.5 w-3.5" /> Exit Preview
          </Button>
        </div>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* SIDEBAR + CONTENT (below top bar) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex flex-1 overflow-hidden relative">
        <aside
          data-testid="sidebar"
          className={`flex flex-col transition-all duration-200 relative ${collapsed ? 'w-0 overflow-hidden border-0' : 'w-[200px] border-r border-white/[0.07]'}`}
          style={{ backgroundColor: 'var(--sidebar-bg)', paddingTop: collapsed ? 0 : '20px' }}
        >
          {/* Permanent 2px brand-blue rule on the LEFT edge of the entire sidebar */}
          {!collapsed && (
            <span
              className="absolute left-0 top-0 bottom-0 bg-primary pointer-events-none"
              style={{ width: '2px' }}
              aria-hidden
            />
          )}
          <ScrollArea className="flex-1">
            <nav className="flex flex-col">
              {/* Standalone items at top: Dashboard, Calendar, Employees (Admin) */}
              {visibleStandalone.map((item) => (
                <SidebarLink key={item.path} item={item} />
              ))}

              {/* Collapsible groups */}
              {visibleGroups.map((group) => {
                const isOpen = !!expandedGroups[group.name];
                const groupSlug = group.name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '');
                return (
                  <div key={group.name} className="mt-1" data-testid={`nav-group-${groupSlug}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.name)}
                      className="w-full flex items-center justify-between text-[12px] tracking-[0.1em] text-muted-foreground/70 font-normal hover:text-muted-foreground transition-colors"
                      style={{
                        paddingTop: '12px',
                        paddingBottom: '6px',
                        paddingLeft: '20px',
                        paddingRight: '16px',
                      }}
                      aria-expanded={isOpen}
                      data-testid={`nav-group-toggle-${groupSlug}`}
                    >
                      <span>{group.name}</span>
                      <ChevronDown
                        className="h-3.5 w-3.5 transition-transform duration-200"
                        style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                        aria-hidden
                      />
                    </button>
                    {isOpen && (
                      <div data-testid={`nav-group-children-${groupSlug}`}>
                        {group.children.map((child) => (
                          <SidebarLink key={child.path} item={child} indented />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* COMING SOON — subtle brand-blue divider above */}
              <div
                className="px-5 mt-4 mb-2 pt-3"
                style={{ borderTop: '4px solid rgba(74, 171, 220, 0.30)' }}
              >
                <span className="text-xs tracking-[0.1em] text-muted-foreground/60">
                  Coming Soon
                </span>
              </div>
              {futureNav.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center text-[15px] text-muted-foreground/40 cursor-not-allowed"
                  style={{ paddingTop: '8px', paddingBottom: '8px', paddingLeft: '20px', paddingRight: '16px' }}
                >
                  {item.name}
                </div>
              ))}
            </nav>
          </ScrollArea>

          {/* User (bottom of sidebar) — avatar circle kept */}
          {user && (
            <div className="p-3 border-t border-white/[0.07]">
              <div
                className="flex items-center gap-2 cursor-pointer rounded-md p-1 -m-1 hover:bg-secondary/40 transition-colors"
                onClick={() => navigate('/profile')}
                data-testid="nav-profile"
              >
                <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                  {user.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {viewAsRole ? (
                      <span className="text-amber-400">Viewing as: {effectiveRole}</span>
                    ) : effectiveRole}
                  </p>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Collapse toggle - floats on sidebar edge */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 z-10 h-6 w-6 text-muted-foreground hover:text-foreground border border-white/10 bg-background"
          style={{ left: collapsed ? '4px' : '188px', transition: 'left 200ms' }}
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </Button>

        {/* MAIN CONTENT */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* "Viewing as" role banner */}
          {viewAsRole && (
            <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-2 flex items-center justify-between" data-testid="viewing-as-banner">
              <div className="flex items-center gap-2">
                <Eye className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-xs text-amber-400 font-medium">
                  Previewing as <span className="font-bold">{viewAsRole}</span>. API access unchanged, UI adjusted to this role&apos;s view
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-amber-400 hover:text-amber-300 text-xs h-6 px-2"
                onClick={() => setViewAsRole(null)}
                data-testid="banner-reset-role-btn"
              >
                <Shield className="h-3 w-3 mr-1" /> Exit Preview
              </Button>
            </div>
          )}
          <main
            className="flex-1 overflow-auto"
            style={{ paddingLeft: '32px', paddingRight: '32px', paddingTop: '28px', paddingBottom: '28px' }}
          >
            <Outlet />
          </main>
        </div>
      </div>
      <ForceRefreshBanner />
    </div>
  );
}



// ─── SidebarLink ──────────────────────────────────────────────────────────
// Single sidebar entry (standalone or grouped child). Indented children sit
// one level deeper. Active state uses the existing brand-blue rule + medium
// weight text style so no other nav pages regress.
function SidebarLink({ item, indented = false }) {
  const slug = item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      data-testid={`nav-${slug}`}
      className={({ isActive }) =>
        `relative flex items-center text-[15px] transition-colors ${
          isActive ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
        }`
      }
      style={{
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: indented ? '32px' : '20px',
        paddingRight: '16px',
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute left-0 top-0 bottom-0 bg-primary"
              style={{ width: '2px' }}
              aria-hidden
            />
          )}
          {item.name}
        </>
      )}
    </NavLink>
  );
}
